import type { ReactNode } from "react";
import { SaveStatus } from "@/components/ui/save-status";
import type { UnitDraft, UnitDraftState } from "@/components/courses/use-unit-draft";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { CourseUnit } from "@/lib/api/courseUnits";

import { PLAN_FIELDS, planWords, planFailureWords, sessionPlanPublication, type PlanFieldKey } from "./session-plan-content";

export function PlanTaskHead({ unit, state, action }: { unit: CourseUnit | null; state?: UnitDraftState; action?: ReactNode }) {
  return <div className="flex flex-wrap items-baseline justify-between gap-2">
    <h3 className="text-ui font-semibold">Session plan</h3>
    <div className="flex items-baseline gap-2"><span className="text-meta text-muted-foreground">{sessionPlanPublication(unit, state)}</span>{action}</div>
  </div>;
}

export function PlanTaskField({ fieldKey, unit, state, sessionId, held = true, onEdit, showSaveStatus = false }: {
  fieldKey: PlanFieldKey; unit: CourseUnit | null; state?: UnitDraftState; sessionId: string; held?: boolean;
  onEdit?: (key: keyof UnitDraft) => void; showSaveStatus?: boolean;
}) {
  const field = PLAN_FIELDS.find(item => item.key === fieldKey)!;
  const value = state?.draft[field.key] ?? planWords(unit?.[field.column]);
  const previous = planWords(unit?.publication?.[field.column]);
  const same = value.trim() === previous.trim();
  const published = !!unit?.publication;
  const id = `session-${sessionId}-${field.key}`;
  const props = {
    id, value, maxLength: field.max, readOnly: !state || state.refused,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { onEdit?.(field.key); state?.setDraft(current => ({ ...current, [field.key]: event.target.value })); },
  };
  return <div data-plan-field={field.key} className="grid gap-1.5">
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <Label htmlFor={id}>{field.label}</Label>
    </div>
    {field.key === "topic" ? <Input {...props} placeholder="What this session covers" /> : <Textarea {...props} rows={field.key === "preparation" ? 3 : 2} />}
    {published && !same && <p className="whitespace-pre-wrap text-meta text-muted-foreground [overflow-wrap:anywhere]">{previous.trim() ? `${!held && ["recap", "followUp"].includes(field.key) ? "Published; students see it once the session is held" : "Students still read"}: ${previous}` : "Students read nothing yet."}</p>}
    {state && showSaveStatus && <PlanTaskSaveStatus state={state} />}
  </div>;
}


/** The shared plan save is reported beside the last field edited. */
export function PlanTaskSaveStatus({ state }: { state: UnitDraftState }) {
  if (state.pending && !state.busy) return null;
  const retry = !state.storageError && !state.conflict && !state.comparisonUnavailable && !!(state.dirty && state.autosavePaused && state.valid);
  const refused = !state.busy && !state.autosaveArmed && state.dirty && state.autosavePaused && !state.pending && !state.conflict && !state.comparisonUnavailable && !state.storageError;
  return <div className="grid gap-1"><SaveStatus status={state.busy || state.autosaveArmed ? { kind: "saving" }
    : state.dirty && state.autosavePaused ? { kind: "failed", reason: state.message || null, retry }
    : state.receipt?.kind === "save" || state.receipt?.kind === "create" ? { kind: "saved" } : null}
    onRetry={() => { if (state.pending) void state.reconcile(); else void state.save(); }} />
    {refused && state.message && <p className="text-meta text-muted-foreground">{planFailureWords(state.message)}</p>}
  </div>;
}

/** The teaching team's private notes, part of the plan, saved with it (O7). */
export function TeachingNotesField({ unit, state, sessionId, onEdit, showSaveStatus = false }: {
  unit: CourseUnit | null; state?: UnitDraftState; sessionId: string;
  onEdit?: (key: keyof UnitDraft) => void; showSaveStatus?: boolean;
}) {
  const id = `session-${sessionId}-notes`;
  return <div data-plan-field="notes" className="grid gap-1.5">
    <Label htmlFor={id}>Teaching notes</Label>
    <p id={`${id}-note`} className="text-meta text-muted-foreground">Teaching team only</p>
    <Textarea id={id} aria-describedby={`${id}-note`} rows={3} maxLength={8000} readOnly={!state || state.refused}
      value={state?.draft.notes ?? unit?.teaching_notes ?? ""}
      onChange={event => { onEdit?.("notes"); state?.setDraft(current => ({ ...current, notes: event.target.value })); }} />
    {state && showSaveStatus && <PlanTaskSaveStatus state={state} />}
  </div>;
}
